import { motion } from "framer-motion";
import { PRODUCTS, waLink, WA_MESSAGES } from "@/lib/site";
import { MessageCircle, ArrowUpRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
};

export const Products = () => (
    <section id="products" className="mx-auto max-w-7xl px-5 py-16 sm:px-8 md:py-24" data-testid="products-section">
        <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-12 flex flex-col gap-4 md:mb-16 md:flex-row md:items-end md:justify-between"
        >
            <div>
                <p className="text-xs font-bold uppercase tracking-[0.32em] text-sky-600">
                    Our crafts
                </p>
                <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] tracking-tight text-sakura-900 sm:text-5xl">
                    Made to carry
                    <span className="italic text-sakura-600"> their name</span>
                </h2>
            </div>
            <p className="max-w-sm text-base leading-relaxed text-sakura-800/85">
                Every piece is printed to order — send us the name, photo or
                words and we do the rest.
            </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 md:gap-8">
            {PRODUCTS.map((p, i) => (
                <motion.article
                    key={p.id}
                    data-testid={`product-card-${p.id}`}
                    {...fadeUp}
                    transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: EASE }}
                    className={`group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-sakura-400/50 bg-white/85 shadow-[0_10px_40px_rgba(162,182,208,0.18)] hover:shadow-[0_24px_60px_rgba(162,182,208,0.32)] transition-shadow duration-300 ${
                        p.id === "mug" ? "md:col-span-2" : ""
                    }`}
                >
                    <div
                        className={`relative overflow-hidden ${
                            p.id === "mug" ? "h-64 sm:h-80 lg:h-[380px]" : "h-60 sm:h-64"
                        }`}
                    >
                        <img
                            src={p.img}
                            alt={p.name}
                            loading="lazy"
                            className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                        />
                        <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700 backdrop-blur">
                            {p.badge}
                        </span>
                    </div>

                    <div className="flex flex-1 flex-col p-6 md:p-7">
                        <h3 className="font-display text-2xl font-semibold text-sakura-900">
                            {p.name}
                        </h3>
                        <p className="mt-2 flex-1 text-[15px] leading-relaxed text-sakura-800/85">
                            {p.desc}
                        </p>
                        <div className="mt-6 flex items-center justify-between gap-3">
                            <p className="font-display text-3xl font-semibold text-sakura-700">
                                {p.price}
                                {p.priceNote && (
                                    <span className="ml-1.5 align-middle font-body text-xs font-medium uppercase tracking-[0.18em] text-sakura-800/70">
                                        {p.priceNote}
                                    </span>
                                )}
                            </p>
                            <a
                                data-testid={`product-order-${p.id}`}
                                href={waLink(p.msg)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 rounded-full bg-sky-600 px-5 py-3 text-sm font-semibold text-white hover:bg-sky-700 transition-colors duration-200"
                            >
                                <MessageCircle size={16} />
                                Order
                            </a>
                        </div>
                    </div>
                </motion.article>
            ))}

            <motion.div
                data-testid="product-card-custom"
                {...fadeUp}
                transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                className="flex h-full flex-col justify-between rounded-[1.75rem] bg-sky-700 p-7 text-white shadow-[0_20px_50px_rgba(107,133,168,0.4)]"
            >
                <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.26em] text-white/80">
                        Something else in mind?
                    </p>
                    <h3 className="mt-3 font-display text-3xl font-semibold leading-tight">
                        If you can dream it,
                        <span className="block italic">we can print it.</span>
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/85">
                        Onesies, bottles, puzzles, photo frames, corporate
                        hampers — chat us your idea for a same-day quote.
                    </p>
                </div>
                <a
                    data-testid="product-order-custom"
                    href={waLink(WA_MESSAGES.custom)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-sky-700 hover:bg-sky-100 transition-colors duration-200"
                >
                    Chat your idea
                    <ArrowUpRight size={16} />
                </a>
            </motion.div>
        </div>
    </section>
);

export default Products;
